import Vue from 'vue'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import './registerServiceWorker'

import routerobj from './router';
import store from './store';
import globals from './plugins/globals';
import freshworks from './plugins/freshworks';
import tokenUtils from './plugins/tokenUtils';
import axioss from 'axios';
import VueAxios from 'vue-axios';
import PerfectScrollbar from 'vue2-perfect-scrollbar';
import 'vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css';
import '@/views';
import './styles/index.scss';
import { loadTheme } from './styles/themes';
import { getApiUrl } from './plugins/serviceUrls';

const requestedTheme = (process.env.VUE_APP_THEME || 'default').toLowerCase();
const normalizedTheme = requestedTheme.replace(/[^a-z0-9]/g, '');
loadTheme(normalizedTheme);

const envFontScale = parseFloat(process.env.VUE_APP_FONT_SCALE || '');
if (!Number.isNaN(envFontScale) && envFontScale > 0 && typeof document !== 'undefined') {
  document.documentElement.style.setProperty('--font-scale', envFontScale);
}

const API_URL = getApiUrl();

Vue.config.productionTip = false

// Configure axios & its middlewares
var axios = axioss.create({
  baseURL: API_URL,
  withCredentials: true
});

const { default: constants } = require('./plugins/constants');
const {
  SESSION_EXPIRED_CODES,
  CLIENT_MISMATCH_CODES,
  REFRESH_EXCLUDE_PATH_FRAGMENTS,
  AUTH_MODES,
} = constants.AUTH;
const AUTH_REFRESH_URL = constants.API_URLS.AUTH_REFRESH;

let isRedirectingToLogin = false;
let refreshPromise = null;

function getRequestUrl(config = {}) {
  const base = config.baseURL || '';
  const url = config.url || '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${base}${url}`;
}

function shouldSkipSilentRefresh(config = {}) {
  if (config?.skipAuthRefresh) return true;
  const fullUrl = getRequestUrl(config).toLowerCase();
  return REFRESH_EXCLUDE_PATH_FRAGMENTS.some((fragment) =>
    fullUrl.includes(String(fragment).toLowerCase()),
  );
}

function isJwtAuthActive() {
  const mode = store.state?.auth?.authMode;
  if (mode === AUTH_MODES.JWT) return true;
  if (mode === AUTH_MODES.SESSION) return false;
  // Infer from stored access token when mode not yet set (e.g. after reload)
  return Boolean(tokenUtils.getSecureToken());
}

/** Active API tenant: soft-switchable `client`, fallback to auth binding. */
function resolveBoundClientId() {
  return store.state.client || store.state.auth?.boundClient || '';
}

function injectClientOnRequest(config = {}) {
  const clientId = resolveBoundClientId();
  if (!clientId) return config;

  const method = String(config.method || 'get').toLowerCase();
  const data = config.data;
  const isPlainObject =
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    !(typeof FormData !== 'undefined' && data instanceof FormData);

  if (isPlainObject && !data.client) {
    data.client = clientId;
  } else if (data == null || data === '') {
    config.data = { client: clientId };
  }

  // GET/DELETE often send `client` via query params.
  if (method === 'get' || method === 'delete' || method === 'head') {
    config.params = config.params || {};
    if (!config.params.client) {
      config.params.client = clientId;
    }
  }
  return config;
}

function isClientMismatchPayload(payload) {
  const code = payload?.code;
  if (CLIENT_MISMATCH_CODES.includes(code)) return true;
  return /api_client_mismatch|tenant_mismatch/i.test(String(payload?.message || ''));
}

function handleSessionExpired() {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  console.warn({ message: 'Session expired', meta: {} });
  try {
    tokenUtils.removeSecureToken();
  } catch (_) { /* storage unavailable */ }
  try {
    store.dispatch('auth/clearSession');
  } catch (_) { /* store may be mid-init */ }
  // Persist a flag so the login screen can show a "session expired" message after reload
  try { sessionStorage.setItem('session_expired', '1'); } catch (_) { /* storage unavailable */ }
  // Keep the current FC cycle URL so post-login navigation can return to the same cycle.
  // Never persist the SSO callback — its query includes a one-time code that must not be replayed.
  const pathname = window.location.pathname || '';
  const isSsoCallback = pathname === constants.SSO.CALLBACK_PATH;
  try {
    const returnPath = `${pathname}${window.location.search || ''}`;
    if (
      returnPath &&
      returnPath !== '/' &&
      pathname !== constants.SSO.LOGOUT_COMPLETE_PATH &&
      !isSsoCallback
    ) {
      sessionStorage.setItem(constants.STORAGE_KEYS.POST_LOGIN_RETURN_PATH, returnPath);
    }
  } catch (_) { /* storage unavailable */ }
  // Full page reload clears all in-memory Vuex state and re-runs bootstrap,
  // ensuring a clean slate identical to a fresh page load. The callback is an
  // exception: reload would re-hit the same consumed code, so go to login instead.
  if (isSsoCallback) {
    window.location.assign('/login');
    return;
  }
  window.location.reload();
}

/** 403 auth.api_client_mismatch — do not keep a stale client binding. */
function handleClientMismatch() {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  console.warn({ message: 'Client mismatch', meta: {} });
  try {
    tokenUtils.removeSecureToken();
  } catch (_) { /* storage unavailable */ }
  try {
    store.dispatch('auth/clearSession');
  } catch (_) { /* store may be mid-init */ }
  try {
    sessionStorage.setItem('auth_client_mismatch', '1');
  } catch (_) { /* storage unavailable */ }
  try {
    sessionStorage.removeItem(constants.STORAGE_KEYS.POST_LOGIN_RETURN_PATH);
  } catch (_) { /* storage unavailable */ }
  window.location.assign('/login');
}

async function silentRefreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = axios
    .post(AUTH_REFRESH_URL, {}, { skipAuthRefresh: true })
    .then((response) => {
      const jwtToken =
        response?.headers?.['x-jwt-token'] || response?.headers?.['X-JWT-Token'];
      if (!jwtToken) {
        console.error({
          message: 'Silent refresh failed',
          statusCode: response?.status,
          meta: { reason: 'missing_jwt' },
        });
        throw new Error('Refresh did not return an access token');
      }
      tokenUtils.storeSecureToken(jwtToken);
      store.commit('auth/setAuthMode', AUTH_MODES.JWT);
      return jwtToken;
    })
    .catch((err) => {
      if (err?.message !== 'Refresh did not return an access token') {
        console.error({
          message: 'Silent refresh failed',
          statusCode: err?.response?.status,
          meta: {},
        });
      }
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

// Add a request interceptor
axios.interceptors.request.use(function (config) {
  injectClientOnRequest(config);
  if (config.method === 'get') {
    config.headers = config.headers || {};
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['If-None-Match'] = '';
  }
  // Attach Bearer when an access token is present (JWT mode inferred)
  try {
    const accessToken = tokenUtils.getSecureToken();
    if (accessToken) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
  } catch (_) { /* ignore token read errors */ }
  return config;
}, function (error) {
  console.log(error);
  return Promise.reject(error);
});

function isSessionExpiredPayload(payload) {
  const code = payload?.code;
  return SESSION_EXPIRED_CODES.includes(code);
}

axios.interceptors.response.use(function (response) {
  try {
    if (isClientMismatchPayload(response?.data)) {
      handleClientMismatch();
      return response;
    }
    if (isSessionExpiredPayload(response?.data)) {
      handleSessionExpired();
    }
    return response;
  } catch (err) {
    console.error('Response interceptor error:', err);
    return response;
  }
}, async function (error) {
  const originalRequest = error?.config || {};
  const status = error?.response?.status;
  let payload = error?.response?.data;

  // Download template uses responseType blob, so UL403 is a Blob, not JSON.
  if (originalRequest.responseType === 'blob' && payload && typeof payload.text === 'function') {
    try {
      const parsed = JSON.parse(await payload.text());
      if (parsed && typeof parsed === 'object') payload = parsed;
    } catch (_err) { /* not JSON */ }
  }

  if (status === 403 && isClientMismatchPayload(payload)) {
    handleClientMismatch();
    return Promise.reject(error);
  }

  const expired = isSessionExpiredPayload(payload) || status === 401;

  if (
    expired &&
    isJwtAuthActive() &&
    !originalRequest._retry &&
    !shouldSkipSilentRefresh(originalRequest)
  ) {
    originalRequest._retry = true;
    try {
      await silentRefreshAccessToken();
      const accessToken = tokenUtils.getSecureToken();
      originalRequest.headers = originalRequest.headers || {};
      if (accessToken) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }
      return axios(originalRequest);
    } catch (refreshErr) {
      handleSessionExpired();
      return Promise.reject(refreshErr);
    }
  }

  if (isSessionExpiredPayload(payload)) {
    handleSessionExpired();
  }

  const serverErrorMessages = error?.response?.data?.message;
  if (serverErrorMessages) error.message = serverErrorMessages;
  console.error('Axios response error:', error?.response?.status || 'Unknown error', error?.message);
  return Promise.reject(error);
});

// Setup other middlewares
Vue.use(VueAxios, axios);
Vue.use(PerfectScrollbar);

// Add tokenUtils to Vue prototype
Vue.prototype.$tokenUtils = tokenUtils;

// Resolve APP_HOST vs DEDICATED before first paint of auth screens
store.dispatch('auth/initHostMode');

let router = routerobj.router;

function mountApp() {
  new Vue({
    router,
    store,
    vuetify,
    render: h => h(App)
  }).$mount('#app');
  Vue.use(globals);
  Vue.use(freshworks);
}

// Register product modules, then mount. Profile hydration lives in base.getDetails
// so we do not block first paint or duplicate POST /v2/profile/getdetails.
addCustomModules()
  .catch((err) => {
    console.log(err);
  })
  .finally(() => {
    mountApp();
  });


// To register the custom modules
async function addCustomModules(){
  // module name, subscription product name, cart group
  await registerCustomeModule("admin", ["admin"], "others");
  await registerCustomeModule("home", ["home"], "ucp");
  await registerCustomeModule("pbc", ["pbc"], "risk");
  await registerCustomeModule("varianceanalysis", ["varianceanalysis"], "accounting");
  await registerCustomeModule("financialclose", ["financialclose"], "accounting");
  await registerCustomeModule("tds", ["tds_ar","tds_ap","tds_monthly"], "compliance");
  await registerCustomeModule("earnout", ["earnout"], "transaction");
  await registerCustomeModule("bot", ["bot"], "others");
  await registerCustomeModule("ia", ["ia"], "risk");
  await registerCustomeModule("helpdesk", ["helpdesk"], "others");
  await registerCustomeModule("flux", ["flux"], "others");
  await registerCustomeModule("chatbot", ["chatbot"], "others");
  await registerCustomeModule("manuals", [], "others");
}

async function registerCustomeModule(name, subscription, solution){
  const {default: modname} = await import('./modules/'+name+'/store');
  store.registerModule("m_"+name, modname);
  const {default: routes} = await import('./modules/'+name+'/route');
  const routelist = registerCustomRouter(routes, name, subscription, solution);
  for (const i of routelist) {
    router.addRoute(i);
  }
  try{
    const {default: config} = await import('./modules/'+name+'/config');
    config.init();
  }catch(err){return}
}

function registerCustomRouter(routes, moduleName, subscription, solution){
  let rt = [];
  for (const el of routes) {
    let nrt = { meta: { ...(el.meta || {}) } };
    if (el.path) {
      if (el.redirect && !el.component) {
        // Redirect-only route (no view component)
        nrt = { path: el.path, redirect: el.redirect, meta: { ...(el.meta || {}) } };
      } else {
        nrt = routerobj.getRouteData(moduleName, solution, el.path, el.component, el.meta, el.function, el.noaccess);
      }
    }
    if (el.name) nrt.name = el.name;
    if (el.children) nrt.children = registerCustomRouter(el.children, moduleName, subscription, solution);
    if (el.redirect) nrt.redirect = el.redirect;
    if (el.right != null) nrt.meta = { ...(nrt.meta || {}), right: el.right };
    nrt.meta = nrt.meta || {};
    nrt.meta.subscription = subscription;
    nrt.meta.solution = solution;
    nrt.meta.modulename = moduleName;
    rt.push(nrt);
  }
  return rt;
}
